/**
 * Context-window usage helpers for Control Tower event timelines.
 *
 * Prefer measured `llm_call.context` from agent-runtime when present
 * (categories were counted from the actual request payload and scaled to
 * billed prompt tokens). Fall back to a timeline heuristic for older runs.
 */

export const DEFAULT_CONTEXT_WINDOW = 200_000;

/** Anthropic-ish cost for a typical full-page screenshot tile. */
export const IMAGE_TOKEN_ESTIMATE = 1_600;

/** Keep only the most recent N screenshot results in the estimate (matches
 * LegacyContextEngine.image_window default of 1). */
export const IMAGE_WINDOW = 1;

/**
 * Model id → context window. Unknown models fall back to DEFAULT_CONTEXT_WINDOW.
 * Keep in sync with agent-runtime loop budget / provider limits.
 */
export const MODEL_CONTEXT_WINDOWS = {
  "claude-sonnet-4": 200_000,
  "claude-sonnet-4-20250514": 200_000,
  "claude-sonnet-4-5": 200_000,
  "claude-sonnet-4-5-20250929": 200_000,
  "claude-opus-4": 200_000,
  "claude-opus-4-20250514": 200_000,
  "claude-haiku-4-5": 200_000,
  "claude-3-5-sonnet": 200_000,
  "claude-3-5-haiku": 200_000,
  "claude-3-opus": 200_000,
};

export const CONTEXT_CATEGORIES = [
  {
    id: "conversation",
    label: "Conversation",
    hint: "User + assistant text turns",
    color: "#4f46e5", // indigo-600
    track: "#e0e7ff",
  },
  {
    id: "tool_results",
    label: "Tool results",
    hint: "Tool arguments and result payloads",
    color: "#0f766e", // teal-700
    track: "#ccfbf1",
  },
  {
    id: "images",
    label: "Images",
    hint: "Screenshots still in the LLM window",
    color: "#b45309", // amber-700
    track: "#fef3c7",
  },
  {
    id: "system",
    label: "System prompt",
    hint: "AGENT.md, skills, memory inlined into the system prompt",
    color: "#475569", // slate-600
    track: "#e2e8f0",
  },
  {
    id: "tool_schemas",
    label: "Tool schemas",
    hint: "JSON tool catalogue sent with the request",
    color: "#64748b", // slate-500
    track: "#f1f5f9",
  },
  {
    id: "other",
    label: "Other",
    hint: "Residual after scaling to billed tokens (tokenizer mismatch)",
    color: "#94a3b8", // slate-400
    track: "#f8fafc",
  },
];

/** @deprecated Use tool_results — kept for reading old estimated snapshots. */
const LEGACY_TOOLS_ID = "tools";

export function resolveContextWindow(model, explicit) {
  if (explicit && Number(explicit) > 0) return Number(explicit);
  if (!model || typeof model !== "string") return DEFAULT_CONTEXT_WINDOW;
  const exact = MODEL_CONTEXT_WINDOWS[model];
  if (exact) return exact;
  const lower = model.toLowerCase();
  for (const [key, size] of Object.entries(MODEL_CONTEXT_WINDOWS)) {
    if (lower.startsWith(key.toLowerCase()) || lower.includes(key.toLowerCase())) {
      return size;
    }
  }
  // Bedrock / dated Anthropic ids often embed the family name.
  if (lower.includes("sonnet") || lower.includes("opus") || lower.includes("haiku")) {
    return DEFAULT_CONTEXT_WINDOW;
  }
  return DEFAULT_CONTEXT_WINDOW;
}

export function charsToTokens(chars) {
  if (!chars || chars < 0) return 0;
  return Math.ceil(chars / 4);
}

export function formatTokenCount(n) {
  if (n == null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(2)}k`;
  return String(Math.round(n));
}

/**
 * Prompt-side tokens that occupy the context window.
 * Anthropic: fresh `input_tokens` + `cache_read_tokens` (cache write is
 * usually already counted in fresh input for the creating turn).
 */
export function getPromptTokens(usage) {
  if (!usage || typeof usage !== "object") return null;
  const fresh = Number(usage.input_tokens ?? usage.prompt_tokens ?? 0) || 0;
  const cacheRead = Number(usage.cache_read_tokens ?? usage.cache_read_input_tokens ?? 0) || 0;
  const total = fresh + cacheRead;
  if (total <= 0 && fresh <= 0 && cacheRead <= 0) {
    if (
      usage.input_tokens != null ||
      usage.prompt_tokens != null ||
      usage.cache_read_tokens != null
    ) {
      return 0;
    }
    return null;
  }
  return total;
}

export function getCacheTokens(usage) {
  if (!usage || typeof usage !== "object") {
    return { read: 0, write: 0, fresh: 0 };
  }
  const fresh = Number(usage.input_tokens ?? usage.prompt_tokens ?? 0) || 0;
  const read = Number(usage.cache_read_tokens ?? usage.cache_read_input_tokens ?? 0) || 0;
  const write =
    Number(usage.cache_write_tokens ?? usage.cache_creation_input_tokens ?? 0) || 0;
  return { read, write, fresh };
}

function textLen(value) {
  if (value == null) return 0;
  if (typeof value === "string") return value.length;
  try {
    return JSON.stringify(value).length;
  } catch {
    return String(value).length;
  }
}

function isScreenshotToolName(name) {
  if (!name) return false;
  const n = String(name).toLowerCase();
  return n.includes("screenshot") || n === "browsing:screenshot";
}

function emptyCategories() {
  return {
    conversation: 0,
    tool_results: 0,
    images: 0,
    system: 0,
    tool_schemas: 0,
    other: 0,
  };
}

/**
 * Normalize measured / estimated category maps into the UI shape.
 * Maps legacy `tools` → `tool_results`.
 */
export function normalizeCategories(raw) {
  const out = emptyCategories();
  if (!raw || typeof raw !== "object") return out;
  for (const key of Object.keys(out)) {
    if (raw[key] != null) out[key] = Math.max(0, Math.round(Number(raw[key]) || 0));
  }
  if (raw[LEGACY_TOOLS_ID] != null && !raw.tool_results) {
    out.tool_results = Math.max(0, Math.round(Number(raw[LEGACY_TOOLS_ID]) || 0));
  }
  // Older estimator dumped schemas into `system`. Keep as-is when no split.
  return out;
}

/**
 * Walk transcript events up to (but not including) `endIndex` and accumulate
 * rough char/token estimates per category.
 *
 * Avoids double-counting tool results: when a `tool_call` carries
 * `pairedResult`, the matching `tool_result` message is skipped for text
 * (screenshot indexing still runs).
 */
export function estimateRawBreakdown(events, endIndex = events.length) {
  const raw = emptyCategories();
  const screenshotIndexes = [];
  const limit = Math.min(endIndex, events.length);

  const pairedResultIds = new Set();
  for (let i = 0; i < limit; i++) {
    const ev = events[i];
    if ((ev?.type || "event") !== "tool_call") continue;
    const fields = ev?._fields || {};
    if (ev.pairedResult && fields.tool_call_id) {
      pairedResultIds.add(fields.tool_call_id);
    }
  }

  for (let i = 0; i < limit; i++) {
    const ev = events[i];
    const type = ev?.type || "event";
    const fields = ev?._fields || {};

    if (type === "message") {
      if (fields.role === "tool_result") {
        const alreadyPaired =
          fields.tool_call_id && pairedResultIds.has(fields.tool_call_id);
        if (!alreadyPaired) {
          raw.tool_results += charsToTokens(textLen(fields.result_text));
        }
        if (isScreenshotToolName(fields.tool_name) || (fields.screenshot_urls || []).length) {
          screenshotIndexes.push(i);
        }
        continue;
      }
      const blocks = fields.blocks || {};
      const texts = blocks.texts || [];
      for (const t of texts) raw.conversation += charsToTokens(textLen(t));
      if (fields.text) raw.conversation += charsToTokens(textLen(fields.text));
      for (const tu of blocks.toolUses || []) {
        raw.tool_results += charsToTokens(textLen(tu.input ?? tu.args));
      }
      const imgCount = (blocks.images || []).length;
      if (imgCount) raw.images += imgCount * IMAGE_TOKEN_ESTIMATE;
      continue;
    }

    if (type === "tool_call") {
      raw.tool_results += charsToTokens(textLen(fields.args));
      const paired = ev.pairedResult;
      if (paired?.result_text) {
        raw.tool_results += charsToTokens(textLen(paired.result_text));
      }
      if (
        isScreenshotToolName(fields.tool_name) ||
        (fields.screenshot_urls || []).length ||
        (paired?.screenshot_urls || []).length
      ) {
        screenshotIndexes.push(i);
      }
      continue;
    }

    if (type === "user_reply" || type === "thinking") {
      raw.conversation += charsToTokens(textLen(fields.text));
      continue;
    }

    if (type === "llm_call") {
      // Schema / system aren't in the event — leave for residual.
      continue;
    }
  }

  const kept = screenshotIndexes.slice(-IMAGE_WINDOW);
  raw.images += kept.length * IMAGE_TOKEN_ESTIMATE;

  return raw;
}

/**
 * Scale raw estimates so they sum to `promptTokens`. Leftover (or deficit)
 * lands in `system` for the heuristic path (system + schemas not in timeline).
 */
export function scaleBreakdownToPrompt(raw, promptTokens) {
  const target = Math.max(0, Math.round(promptTokens || 0));
  const categories = normalizeCategories(raw);
  // Heuristic path cannot see schemas — keep tool_schemas at 0 and put
  // residual into system (historical behaviour).
  const known = Math.max(
    0,
    categories.conversation + categories.tool_results + categories.images
  );

  if (target === 0) {
    return emptyCategories();
  }

  if (known === 0) {
    return { ...emptyCategories(), system: target };
  }

  if (known <= target) {
    return {
      ...emptyCategories(),
      conversation: categories.conversation,
      tool_results: categories.tool_results,
      images: categories.images,
      system: target - known,
    };
  }

  const scale = target / known;
  const conversation = Math.round(categories.conversation * scale);
  const tool_results = Math.round(categories.tool_results * scale);
  const images = Math.round(categories.images * scale);
  const used = conversation + tool_results + images;
  return {
    ...emptyCategories(),
    conversation,
    tool_results,
    images,
    system: Math.max(0, target - used),
  };
}

/**
 * Prefer runtime-measured context on the llm_call event when present.
 */
export function categoriesFromMeasuredContext(context) {
  if (!context || typeof context !== "object") return null;
  const cats = context.categories;
  if (!cats || typeof cats !== "object") return null;
  return normalizeCategories(cats);
}

/**
 * Build the full snapshot used by the ContextMeter UI.
 *
 * @param {object} opts
 * @param {object|null} opts.usage - TokenUsage from an llm_call
 * @param {array} [opts.events] - prepared timeline events for this run
 * @param {number} [opts.eventIndex] - index of the event that carries llmMeta
 *   (breakdown uses events *before* this index)
 * @param {number} [opts.contextWindow]
 * @param {object} [opts.llmMeta] - optional full llm_call fields (cost, model, context…)
 */
export function buildContextSnapshot({
  usage,
  events = [],
  eventIndex = 0,
  contextWindow,
  llmMeta = null,
} = {}) {
  const promptTokens = getPromptTokens(usage);
  if (promptTokens == null) return null;

  const model = llmMeta?.model || null;
  const measuredContext = llmMeta?.context || null;
  const measuredCats = categoriesFromMeasuredContext(measuredContext);
  const measured = Boolean(measuredCats);

  const windowSize =
    (measuredContext?.window && Number(measuredContext.window)) ||
    resolveContextWindow(model, contextWindow);

  const cache = getCacheTokens(usage);
  const outputTokens =
    Number(usage?.output_tokens ?? usage?.completion_tokens ?? 0) || 0;

  let categories;
  if (measuredCats) {
    const sum = CONTEXT_CATEGORIES.reduce((s, c) => s + (measuredCats[c.id] || 0), 0);
    if (promptTokens > 0 && sum !== promptTokens) {
      categories = scaleMeasuredToPrompt(measuredCats, promptTokens);
    } else {
      categories = measuredCats;
    }
  } else {
    const raw = estimateRawBreakdown(events, eventIndex);
    categories = scaleBreakdownToPrompt(raw, promptTokens);
  }

  const pct = windowSize > 0 ? Math.min(100, (promptTokens / windowSize) * 100) : 0;
  const cacheHitPct =
    promptTokens > 0 ? Math.min(100, (cache.read / promptTokens) * 100) : 0;

  return {
    promptTokens,
    outputTokens,
    windowSize,
    pct,
    cache,
    cacheHitPct,
    categories,
    model,
    costUsd: llmMeta?.cost_usd ?? null,
    durationMs: llmMeta?.duration_ms ?? null,
    estimated: !measured,
    measured,
    engine: measuredContext?.engine || null,
    compressed: Boolean(measuredContext?.compressed),
  };
}

function scaleMeasuredToPrompt(cats, promptTokens) {
  const target = Math.max(0, Math.round(promptTokens || 0));
  const keys = CONTEXT_CATEGORIES.map((c) => c.id);
  const known = keys.reduce((s, k) => s + (cats[k] || 0), 0);
  if (target === 0 || known === 0) {
    return { ...emptyCategories(), other: target };
  }
  const scale = target / known;
  const out = emptyCategories();
  for (const k of keys) {
    out[k] = Math.round((cats[k] || 0) * scale);
  }
  const used = keys.reduce((s, k) => s + out[k], 0);
  out.other = Math.max(0, out.other + (target - used));
  return out;
}

/**
 * Last llm_call in a run's raw event list — used for the subchat header
 * "final context" meter.
 *
 * @param {array} events
 * @param {(ev: object) => object} getFields - extracts compact fields (usage, model…)
 * @returns {{ fields: object, index: number } | null}
 */
export function findFinalLlmCall(events, getFields) {
  if (!Array.isArray(events) || !events.length || typeof getFields !== "function") {
    return null;
  }
  let best = null;
  for (let i = 0; i < events.length; i++) {
    const ev = events[i];
    if ((ev?.type || "event") !== "llm_call") continue;
    const fields = getFields(ev) || {};
    if (!fields.usage) continue;
    best = { fields, index: i };
  }
  return best;
}
